import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Divider,
  IconButton,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs, { Dayjs } from "dayjs";
import { useCreateAnonymousBooking } from "src/data/booking/queries/booking";
import { AnonymousBookingDraft } from "src/business/booking";
import { toLocalDateTime } from "src/Utils/datetime";
import { CustomLoadingButton, SnackBar } from "src/design";
import {
  colorBorder,
  colorSurface,
  colorSurfaceHover,
  colorTextMuted,
  colorTextPrimary,
  colorTextSecondary,
  radiusCard,
  shadowCard,
} from "src/design/tokens";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  restaurantId: string;
  onSuccess?: () => void;
  // When true the date/time picker is hidden and the booking is created for
  // `bookingDate` (used by the table-schedule placement flow, where the slot —
  // not the modal — sets the time).
  hideDateTime?: boolean;
  bookingDate?: Dayjs;
  // When provided, the modal does NOT create the booking on submit. Instead it
  // hands the collected details back so the caller can create it later with the
  // final date/time and table (table-schedule placement flow).
  onSubmitDraft?: (draft: AnonymousBookingDraft) => void;
  // Short line naming the slot the booking will land on, shown under the
  // subtitle when the modal was opened by picking a cell on a schedule board.
  slotHint?: string;
}

export const CreateBookingModal = ({
  open,
  onClose,
  restaurantId,
  onSuccess,
  hideDateTime = false,
  bookingDate,
  onSubmitDraft,
  slotHint,
}: Props) => {
  const { t } = useTranslation();
  const [successSnack, setSuccessSnack] = useState(false);
  const [errorSnack, setErrorSnack] = useState(false);

  const { mutate: createBooking, isPending } = useCreateAnonymousBooking(
    () => {
      setSuccessSnack(true);
      onSuccess?.();
      onClose();
    },
    () => setErrorSnack(true),
  );

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("MA");
  const [numberOfSeats, setNumberOfSeats] = useState<number | "">(2);
  const [date, setDate] = useState<Dayjs | null>(
    bookingDate ?? dayjs().add(1, "hour").startOf("hour"),
  );
  const [notes, setNotes] = useState("");
  const [error, setError] = useState(false);

  const isValid =
    firstName.trim() &&
    lastName.trim() &&
    phone.trim() &&
    numberOfSeats &&
    date;

  const handleSubmit = () => {
    if (!isValid) {
      setError(true);
      return;
    }

    const details = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      country,
      restaurantId,
      numberOfSeats: Number(numberOfSeats),
      // Deal attribution is resolved at placement time from the chosen slot, so
      // the draft starts with no deal.
      dealId: null,
      dealType: null,
      notes: notes.trim() || null,
    };

    // Placement flow: defer creation to the caller, which sets the final
    // date/time and table once a cell is chosen.
    if (onSubmitDraft) {
      onSubmitDraft(details);
      onClose();
      return;
    }

    createBooking({ ...details, date: toLocalDateTime(date!) });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: radiusCard,
            boxShadow: shadowCard,
            border: `1px solid ${colorBorder}`,
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "18px", lineHeight: 1.3 }}
              >
                {t("bookings.createModal.title")}
              </Typography>
              <Typography
                sx={{ fontSize: "13px", color: colorTextMuted, mt: 0.25 }}
              >
                {t("bookings.createModal.subtitle")}
              </Typography>
              {slotHint && (
                <Typography
                  sx={{
                    fontSize: "12.5px",
                    fontWeight: 600,
                    color: colorTextPrimary,
                    mt: 0.5,
                  }}
                >
                  {slotHint}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ mt: -0.5, mr: -1 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider sx={{ mx: 3, mt: 1.5 }} />

        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("bookings.createModal.firstName")}
                  required
                  fullWidth
                  size="small"
                  value={firstName}
                  error={error && !firstName.trim()}
                  onChange={(e) => setFirstName(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("bookings.createModal.lastName")}
                  required
                  fullWidth
                  size="small"
                  value={lastName}
                  error={error && !lastName.trim()}
                  onChange={(e) => setLastName(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("bookings.createModal.email")}
                  type="email"
                  fullWidth
                  size="small"
                  value={email}
                  error={false}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("bookings.createModal.phone")}
                  required
                  fullWidth
                  size="small"
                  value={phone}
                  error={error && !phone.trim()}
                  onChange={(e) => setPhone(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("bookings.createModal.numberOfSeats")}
                  type="number"
                  required
                  fullWidth
                  size="small"
                  value={numberOfSeats}
                  error={error && !numberOfSeats}
                  onChange={(e) =>
                    setNumberOfSeats(
                      e.target.value === "" ? "" : Number(e.target.value),
                    )
                  }
                  inputProps={{ min: 1, max: 50 }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t("bookings.createModal.country")}
                  fullWidth
                  size="small"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Grid>
              {!hideDateTime && (
                <Grid item xs={12}>
                  <DateTimePicker
                    label={t("bookings.createModal.date")}
                    value={date}
                    onChange={setDate}
                    minutesStep={30}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        error={error && !date}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label={t("bookings.createModal.notes")}
                  fullWidth
                  size="small"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Grid>
            </Grid>

            <Divider />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Box
                component="button"
                onClick={onClose}
                sx={{
                  px: 2.5,
                  py: 1,
                  fontSize: "14px",
                  fontWeight: 500,
                  border: `1px solid ${colorBorder}`,
                  borderRadius: "8px",
                  backgroundColor: colorSurface,
                  cursor: "pointer",
                  color: colorTextSecondary,
                  "&:hover": { backgroundColor: colorSurfaceHover },
                }}
              >
                {t("common.cancel")}
              </Box>
              <CustomLoadingButton
                loading={isPending}
                onSubmit={handleSubmit}
                label={t("bookings.createModal.submitButton")}
                variant="contained"
                color="success"
                fullwidth={false}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <SnackBar
        open={successSnack}
        onClose={() => setSuccessSnack(false)}
        message={t("bookings.createModal.success")}
        severity="success"
      />
      <SnackBar
        open={errorSnack}
        onClose={() => setErrorSnack(false)}
        message={t("bookings.createModal.error")}
        severity="error"
      />
    </LocalizationProvider>
  );
};
